import * as _ from 'lodash';

import nvSharedModelsModule from '../nv.shared.models.module';

angular
  .module(nvSharedModelsModule)
  .factory('Student', function(
    ImStudent,
    ImStudentRegents,
    ImStudentCreditGaps,
    ImStudentGradPlanning,
    ImStudentCurrentProgram,
    UtilitiesService,
    NvBaseModel,
    ErrorModal,
    $http,
    PublicConfig,
    enqueuedPatchFieldsMixin,
    Rollbar,
  ) {
    let Student = NvBaseModel(
      '/v1/students/:id',
      { id: '@_id', schoolId: '@schoolId' },
      {
        search: {
          method: 'POST',
          isArray: true,
          url: PublicConfig.NV_API_ORIGIN + '/v1/students/search',
          transformRequest(data) {
            if (data) {
              data = angular.toJson(data);
            }
            return data;
          },
          transformResponse(data) {
            data = angular.fromJson(data);

            // if the request is cancelled, data will be null
            if (data) {
              return Student.parseListSearchData(data.data);
            }
          },
        },
      },
    );

    Student.extend({
      // Let ImStudent know that it should never memoize these students, since they are mutable
      _mutable: true,
    });

    /**
     * Update a single student
     * @param id {string}
     * @param path {string} dot notation path to change
     * @param newValue {any} new value to set at path
     * @returns {HttpPromise}
     */
    Student.singleUpdate = function(id, path, newValue) {
      let patch = UtilitiesService.setFieldByPath({}, path, newValue);
      return $http.patch(PublicConfig.NV_API_ORIGIN + '/v1/students/' + id, patch);
    };

    /**
     * Bulk update a set of students
     * @param ids {array} list of student _ids
     * @param path {string} dot notation path to change
     * @param newValue {any} new value to set at path
     * @returns {HttpPromise}
     */
    Student.bulkUpdate = function(ids, path, newValue) {
      return Student.multiBulkUpdate([{ _ids: ids, path, newValue }]);
    };

    /**
     * Updates multiple students with multiple patches
     *
     * @param patches {Array} an array of patches that look like this:
     *    @example Patches
     *       [
     *          {
     *             _ids: ['800009', '74638284'],
     *             path: 'nextScheduledRegents.ccEla.adminDate',
     *             newValue: '2017-06-22'
     *          }
     *       ]
     * @returns {HttpPromise}
     */
    Student.multiBulkUpdate = function(patches) {
      const backendPatches = _.map(patches, (patch: any) => {
        return {
          studentIds: patch._ids,
          patch: UtilitiesService.setFieldByPath({}, patch.path, patch.newValue),
        };
      });
      return $http.patch(PublicConfig.NV_API_ORIGIN + '/v1/students/bulk', {
        patches: backendPatches,
      });
    };

    Student.placePublicImStudentStaticMethodsOnPrototype = function() {
      // We need to use .prototype on ImStudent because ImStudent is an instance of ImStudent, and therefore has none
      // of its own properties.
      const ImStudentModels = [ImStudentRegents, ImStudentCreditGaps, ImStudentGradPlanning, ImStudentCurrentProgram];
      // loop over each student model:
      _.forEach(ImStudentModels, model => {
        // get array of methods
        let ImStudentStaticMethods;
        if (model === ImStudentCreditGaps) {
          ImStudentStaticMethods = [];
          // tslint:disable-next-line:forin
          for (let method in ImStudentCreditGaps) {
            ImStudentStaticMethods.push(method);
          }
        } else if (model === ImStudentRegents) {
          ImStudentStaticMethods = [];
          for (let method in ImStudentRegents) {
            // tslint:disable-next-line:forin
            ImStudentStaticMethods.push(method);
          }
        } else if (model === ImStudentCurrentProgram) {
          ImStudentStaticMethods = [];
          for (let method in ImStudentCurrentProgram) {
            // tslint:disable-next-line:forin
            ImStudentStaticMethods.push(method);
          }
        } else {
          ImStudentStaticMethods = _.difference(Object.getOwnPropertyNames(model), ['length', 'name', 'prototype']);
        }
        // remove ImStudent private methods
        _.remove(ImStudentStaticMethods, (staticMethod: any) => {
          return staticMethod.indexOf('_') === 0;
        });

        _.forEach(ImStudentStaticMethods, staticMethod => {
          // temporarily handle dups:
          if (Student.prototype[staticMethod]) return;
          if (Student.prototype[staticMethod]) {
            throw new Error(`ImStudent.${staticMethod} static method already exists in Student.prototype`);
          }

          Student.prototype[staticMethod] = function(...params) {
            params.unshift(this);
            Rollbar.warning(`The method ${staticMethod} should by called from an ImModel, not Student`);
            return model[staticMethod].apply(model, params);
          };
        });
      });
    };

    /**
     * @param data {Object} with paths and values
     */
    Student.parseListSearchData = function(data, projection) {
      const paths = data.paths;
      const values = data.values;
      const pathsToGet = projection && _.keys(projection);
      const arrayToObjectFunction = UtilitiesService.buildArrayToObjectFunction(paths, pathsToGet);

      return _.map(values, arrayToObjectFunction);
    };

    Student.placePublicImStudentStaticMethodsOnPrototype();

    return enqueuedPatchFieldsMixin(Student);
  });
